import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Gallery.css';

interface GalleryImage {
  filename: string;
  url: string;
}

const Gallery: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/gallery');
      if (response.ok) {
        const data = await response.json();
        setImages(data || []);
        setError(null);
      } else {
        throw new Error('Failed to fetch');
      }
    } catch (err) {
      setError('Nem sikerült betölteni a képeket. Kérjük, próbálja újra később.');
      console.error('Error fetching images:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (imagePath: string) => {
    setSelectedImage(imagePath);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'auto';
  };

  // Helper to resolve image URL
  const getImageUrl = (path: string) => {
    return path;
  };

  const heroImage = "https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?q=80&w=2000&auto=format&fit=crop"; 

  return (
    <div className="gallery-page">
      {/* Hero Section */}
      <section className="gallery-hero" style={{ backgroundImage: `url(${heroImage})` }}>
        <div className="hero-overlay"></div>
        <div className="hero-content fade-in-up">
          <h1 className="hero-title">Munkáim Galériája</h1>
          <p className="hero-subtitle">Minőség • Részletek • Inspiráció</p>
        </div>
      </section>

      <div className="container">
        {/* Intro */}
        <section className="gallery-intro fade-in-up">
          <h2 className="section-title center">Referencia Munkák</h2>
          <p className="section-desc center">
            Büszke vagyok minden elvégzett munkámra. Az alábbi galériában megtekintheti 
            legutóbbi projektjeimet, melyek hűen tükrözik a precizitást és szakértelmet.
          </p>
        </section>

        {/* Gallery Grid */}
        <div className="gallery-content">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Galéria betöltése...</p>
            </div>
          ) : error ? (
            <div className="error-message center">
              <p>{error}</p>
              <button onClick={fetchImages} className="btn btn-outline">Újrapróbálás</button>
            </div>
          ) : images.length === 0 ? (
            <div className="empty-state fade-in-up">
              <div className="empty-icon">📷</div>
              <h3>Hamarosan érkeznek a képek</h3>
              <p>Jelenleg nincs feltöltött munka a galériában. Kérjük, látogasson vissza később!</p>
            </div>
          ) : (
            <div className="gallery-grid">
              {images.map((image, index) => (
                <div 
                  key={image.filename} 
                  className="gallery-item fade-in-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  onClick={() => openModal(image.url)}
                >
                  <div className="image-aspect-ratio">
                    <img 
                      src={image.url} 
                      alt="Referencia munka" 
                      loading="lazy"
                    />
                  </div>
                  <div className="item-overlay">
                    <span className="zoom-icon">🔍</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* CTA Section */}
        <section className="gallery-footer-cta fade-in-up">
          <h3>Tetszenek a munkáim?</h3>
          <p>Kérjen ingyenes felmérést és árajánlatot még ma!</p>
          <Link to="/kapcsolat" className="btn btn-primary">Ajánlatkérés</Link>
        </section>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="lightbox" onClick={closeModal}>
          <button className="close-btn" onClick={closeModal}>×</button>
          <div className="lightbox-content" onClick={e => e.stopPropagation()}>
            <img 
              src={selectedImage} 
              alt="Nagyított kép" 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
